import * as React from 'react';
import { useEffect, useRef } from 'react';

import PropTypes from 'prop-types';
import { compose, getContext } from 'recompose';

import type { ActionContext } from 'js/lib/ActionContext';
import connectToStores from 'js/lib/connectToStores';
import retracked from 'js/lib/retracked';
import { useLocation } from 'js/lib/useRouter';

import { trackDegreeEOICompletion } from 'bundles/event-tracking/lib';
import EoiBody from 'bundles/expression-of-interest/components/eoi-v1/EoiBody';
import EoiFooter from 'bundles/expression-of-interest/components/eoi-v1/EoiFooter';
import EoiHeader from 'bundles/expression-of-interest/components/eoi-v1/EoiHeader';
import { EOI_SUBMISSION_TRACKER } from 'bundles/expression-of-interest/constants';
import ProgramQualificationActions from 'bundles/university-program-qualification/actions/ProgramQualificationActions';
import ProgramQualificationStore from 'bundles/university-program-qualification/store/ProgramQualificationStore';
import { DegreeQualifiedTitle, SectionTypes } from 'bundles/university-program-qualification/utils/constants';
import type { EoiSectionPropTypes } from 'bundles/xddp/types';

import _t from 'i18n!nls/xddp';

import 'css!bundles/xddp/components/__styles__/eoi/EoiForm';

const { previewQualificationSurvey, submitResponse, updateResponse } = ProgramQualificationActions;

type InputProps = {
  partnerName?: string;
  productId: string;
  productType: string;
  slug: string;
  onSubmitSuccess?: (responseId?: string) => void;
  prePopulatedResponse?: {};
  eoiConfirmationText?: string;
};

type PropsFromGetContext = {
  executeAction: ActionContext['executeAction'];
  _eventData?: Parameters<typeof retracked.trackComponent>[0];
};

type PropsFromStores = {
  section: EoiSectionPropTypes;
  responseId: string;
  isSubmitting: boolean;
};

export type Props = InputProps & PropsFromGetContext & PropsFromStores;

export const EoiForm = ({
  partnerName,
  productId,
  productType,
  slug,
  onSubmitSuccess,
  prePopulatedResponse,
  eoiConfirmationText,
  section,
  responseId,
  isSubmitting,
  executeAction,
  _eventData,
}: Props): JSX.Element | null => {
  const surveyRef = useRef<HTMLDivElement | null>(null);
  const location = useLocation();

  useEffect(() => {
    executeAction(previewQualificationSurvey, {
      slug,
      productId,
      productType,
      onSuccess: () => {
        if (prePopulatedResponse) executeAction(updateResponse, prePopulatedResponse);
      },
    });
  }, [slug, productId, productType, prePopulatedResponse, executeAction]);

  const onError = () => {
    if (surveyRef.current) {
      const errorElement = surveyRef.current.querySelector(
        '.rc-SurveySection .section-questions.horizontal-box .rc-SurveyQuestion .rc-TextInputElement.error-state input'
      ) as HTMLDivElement;

      if (errorElement) {
        errorElement.focus();
      }
    }
  };

  const onNextClick = () => {
    executeAction(submitResponse, {
      onSuccess: (id) => {
        trackDegreeEOICompletion(slug, id);
        if (onSubmitSuccess) {
          onSubmitSuccess(id);
        }
      },
      onError: () => onError(),
      logAction: (action, metadata) =>
        retracked.trackComponent(
          _eventData,
          { pathname: location.pathname, ...metadata },
          EOI_SUBMISSION_TRACKER,
          action
        ),
    });
  };

  const isTerminalSection = section && section.sectionType === SectionTypes.TERMINAL;
  const isQualified = isTerminalSection && section.title === DegreeQualifiedTitle;

  return (
    <div id="eoi" className="rc-EoiForm">
      {!section ? (
        <div className="headline-2-text loading-area">{_t('Loading...')}</div>
      ) : (
        <div ref={surveyRef}>
          <EoiHeader section={section} showDescription={true} />
          <EoiBody
            section={section}
            isSubmitting={isSubmitting}
            slug={slug}
            eoiConfirmationText={eoiConfirmationText}
          />
          <EoiFooter
            partnerName={partnerName}
            responseId={responseId}
            isTerminalSection={isTerminalSection}
            isQualified={isQualified}
            isSubmitting={isSubmitting}
            onNextClick={onNextClick}
            consentMessage={section.consentMessage}
            nextMessage={section.nextMessage}
            trackingProductId={productId.replace('base~', '') || ''}
          />
        </div>
      )}
    </div>
  );
};

export default compose<Props, InputProps>(
  connectToStores<Props, InputProps>([ProgramQualificationStore], (storeInstance, props) => ({
    ...props,
    section: storeInstance.getSectionData(),
    responseId: storeInstance.getResponseId(),
    isSubmitting: storeInstance.getIsSubmitting(),
  })),
  getContext({
    executeAction: PropTypes.func.isRequired,
    _eventData: PropTypes.object,
  })
)(EoiForm);
